import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CaseIndexEntryView, CaseIndexPhaseView, CaseRetentionView, IndexVerificationReport, PhaseVerification, VerificationCheck, VerificationStatus } from '@pqrs/contracts';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Label } from '@xiltrion/ui/label';
import { Switch } from '@xiltrion/ui/switch';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@xiltrion/ui/sheet';
import { Spinner } from '@xiltrion/ui/spinner';
import { Tabla, type ColumnaTabla } from '@xiltrion/ui/bloques/tabla';
import { Textarea } from '@xiltrion/ui/textarea';
import { Input } from '@xiltrion/ui/input';
import { useNavigate } from 'react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from '@xiltrion/ui/tooltip';
import type { CaseDetail } from '@/api/types';
import { Card, EmptyState, ErrorState, Field, KeyValue, LoadingState, fmtDate } from '@/components/ui';
import { useCaseIndex, useDisposeCase, useRetryIndexSignature, useVerifyIndex } from '../api';
import { copyLabel, errorMessage, fmtBytes, notify, versionLabel } from '../lib';
import { IntegrityCheckAnimation } from './IntegrityCheckAnimation';
import { ReopenDialog } from './ReopenDialog';

const PHASE_STATUS: Record<CaseIndexPhaseView['status'], { label: string; variant: 'info-light' | 'warning-light' | 'success-light' | 'destructive-light'; icon: NombreIcono }> = {
  OPEN: { label: 'Abierto', variant: 'info-light', icon: 'mdi:folder-open-outline' },
  SEALING: { label: 'Firmando…', variant: 'warning-light', icon: 'mdi:timer-sand' },
  SIGNED: { label: 'Firmado', variant: 'success-light', icon: 'mdi:check-decagram' },
  FAILED: { label: 'Firma fallida', variant: 'destructive-light', icon: 'mdi:alert-circle-outline' },
};
const REVOCATION: Record<string, string> = { GOOD: 'Vigente (no revocado)', REVOKED: 'Revocado', UNKNOWN: 'Desconocido para el emisor', UNAVAILABLE: 'Sin consulta posible' };
const CHECK: Record<VerificationStatus, { icon: NombreIcono; cls: string; label: string }> = {
  OK: { icon: 'mdi:check-circle', cls: 'text-success', label: 'Cumple' },
  FAIL: { icon: 'mdi:close-circle', cls: 'text-destructive', label: 'No cumple' },
  WARN: { icon: 'mdi:alert', cls: 'text-warning', label: 'Con reservas' },
  NA: { icon: 'mdi:minus-circle-outline', cls: 'text-muted-foreground', label: 'No aplica' },
};
const TERMINAL = ['RESOLVED', 'CLOSED', 'ARCHIVED'];
const shortHash = (h: string) => `${h.slice(0, 12)}…${h.slice(-4)}`;

/**
 * Índice electrónico del expediente (Acuerdo 001 de 2024 AGN): cada documento con su número y su huella,
 * la firma del cierre, la verificación de auditoría y la reapertura con índice complementario.
 */
export function IndexTab({ c }: { c: CaseDetail }) {
  const q = useCaseIndex(c.id);
  const verify = useVerifyIndex(c.id);
  const [report, setReport] = useState<IndexVerificationReport | null>(null);
  const [verifyError, setVerifyError] = useState<unknown>(null);
  /** Cada verificación es una corrida nueva: reinicia la presentación del panel. */
  const [run, setRun] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [disposing, setDisposing] = useState(false);
  const closed = TERMINAL.includes(c.status);
  const last = q.data?.phases[q.data.phases.length - 1];
  const documentos = (q.data?.phases ?? []).reduce((n, p) => n + p.entries.length, 0);
  const runVerify = () => {
    setReport(null);
    setVerifyError(null);
    setRun((n) => n + 1);
    setSheetOpen(true);
    verify.mutate(undefined, { onSuccess: setReport, onError: setVerifyError });
  };

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar el índice electrónico." />;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-3">
        <p className="text-sm text-muted-foreground m-0 flex-1 min-w-64">
          Relación de los documentos del expediente en el orden en que entraron, cada uno con su huella SHA-256. Al cerrar el expediente el índice se firma digitalmente y queda congelado; si se reabre, se abre un índice complementario que sigue la numeración.
          {q.data.trdCode ? <> Serie/subserie TRD: <span className="font-medium text-foreground">{q.data.trdCode}</span>.</> : <> El tipo de caso no tiene código TRD.</>}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline"><a href={`/api/v1/archive/cases/${c.id}/export.zip`} download><Icono nombre="mdi:folder-zip-outline" /> Descargar expediente</a></Button>
          {c.can.audit && <Button variant="outline" onClick={runVerify} disabled={verify.isPending}>{verify.isPending ? <Spinner /> : <Icono nombre="mdi:shield-search" />} Verificar integridad</Button>}
          {closed && c.can.manage && <Button variant="outline" onClick={() => setReopening(true)} disabled={last?.status !== 'SIGNED'} title={last?.status !== 'SIGNED' ? 'Se puede reabrir cuando el índice del cierre esté firmado' : undefined}><Icono nombre="mdi:folder-refresh-outline" /> Reabrir expediente</Button>}
          {q.data.retention.canDispose && <Button variant="destructive" onClick={() => setDisposing(true)}><Icono nombre="mdi:delete-forever-outline" /> Eliminar expediente</Button>}
        </div>
      </div>
      <RetentionNotice r={q.data.retention} />
      {!q.data.phases.length ? <EmptyState title="El expediente todavía no tiene índice." hint="Se abre con el primer documento y se firma al cerrar." icon="mdi:format-list-numbered" /> : (
        [...q.data.phases].reverse().map((p) => <PhaseCard key={p.id} caseId={c.id} p={p} total={q.data.phases.length} canManage={c.can.manage} />)
      )}
      <VerificationSheet open={sheetOpen} onHide={() => setSheetOpen(false)} run={run} report={report} error={verifyError} fases={q.data.phases.length} documentos={documentos} />
      <ReopenDialog caseId={c.id} open={reopening} onHide={() => setReopening(false)} />
      <DisposeDialog caseId={c.id} radication={c.radication ?? c.id} r={q.data.retention} open={disposing} onHide={() => setDisposing(false)} />
    </div>
  );
}

/** La fase solo se nombra cuando hay reaperturas: un expediente sin ellas tiene un único índice. */
const phaseTitle = (phase: number, total: number) => {
  const name = phase === 1 ? 'Índice del expediente' : 'Índice complementario';
  return total > 1 ? `Fase ${phase} · ${name}` : name;
};

function PhaseCard({ caseId, p, total, canManage }: { caseId: string; p: CaseIndexPhaseView; total: number; canManage: boolean }) {
  const retry = useRetryIndexSignature(caseId);
  const st = PHASE_STATUS[p.status];
  const signed = p.status === 'SIGNED';
  /** Agrupar los documentos por tipología: para ver qué hay en el expediente sin perder la numeración dentro de cada grupo. */
  const [porTipo, setPorTipo] = useState(false);
  const tipos = useMemo(() => new Set(p.entries.map((e) => e.documentType)).size, [p.entries]);
  const columnas = useMemo<ColumnaTabla<CaseIndexEntryView>[]>(() => [
    { key: 'order', titulo: 'N.º', valor: (e) => e.order, render: (e) => <span className="tnum font-medium">{e.order}</span>, numerico: true, ancho: '3.5rem' },
    { key: 'type', titulo: 'Tipo documental', valor: (e) => e.documentType },
    { key: 'file', titulo: 'Documento', valor: (e) => e.fileName, render: (e) => (
      <span className="flex flex-wrap items-center gap-2">
        <span className={`truncate max-w-xs ${e.annulledAt ? 'line-through text-muted-foreground' : ''}`} title={e.fileName}>{e.fileName}</span>
        {e.copyMethod && <Badge variant="info-light" size="sm">{copyLabel(e.copyMethod, e.copyOfOrder)}</Badge>}
        {e.replacesOrder && <Badge variant="primary-light" size="sm">{versionLabel(e.documentVersion, e.replacesOrder)}</Badge>}
        {e.annulledAt && <Tooltip><TooltipTrigger asChild><Badge variant="destructive-light" size="sm" tabIndex={0}>Anulado</Badge></TooltipTrigger><TooltipContent>{fmtDate(e.annulledAt)} · {e.annulReason}</TooltipContent></Tooltip>}
      </span>
    ) },
    { key: 'date', titulo: 'Fecha de ingreso', valor: (e) => e.incorporatedAt, render: (e) => <span className="whitespace-nowrap tnum">{fmtDate(e.incorporatedAt)}</span> },
    { key: 'pages', titulo: 'Páginas', valor: (e) => e.pageCount ?? 0, render: (e) => <span className="tnum">{e.pageCount ?? '—'}</span>, numerico: true },
    { key: 'format', titulo: 'Formato', valor: (e) => e.format, render: (e) => <span className="text-xs">{e.format}</span> },
    { key: 'size', titulo: 'Tamaño', valor: (e) => e.sizeBytes, render: (e) => <span className="tnum">{fmtBytes(e.sizeBytes)}</span>, numerico: true },
    { key: 'hash', titulo: 'Huella SHA-256', valor: (e) => e.hash, render: (e) => <Tooltip><TooltipTrigger asChild><span className="text-xs tnum text-muted-foreground" tabIndex={0}>{shortHash(e.hash)}</span></TooltipTrigger><TooltipContent className="max-w-none break-all">{e.hash}</TooltipContent></Tooltip> },
  ], []);
  return (
    <Card
      title={<span className="flex items-center gap-2">{phaseTitle(p.phase, total)} <Badge variant={st.variant} className="gap-1">{p.status === 'SEALING' ? <Spinner className="size-3" /> : <Icono nombre={st.icon} aria-hidden />} {st.label}</Badge></span>}
      actions={<Button asChild variant="outline" size="sm"><a href={`/api/v1/archive/cases/${caseId}/index/${p.phase}/xml`} download><Icono nombre="mdi:download" /> {signed ? 'XML firmado' : 'XML (sin firma)'}</a></Button>}
    >
      <div className="flex flex-col gap-3">
        {p.status === 'FAILED' && (
          <Alert variant="destructive">
            <Icono nombre="mdi:alert-circle-outline" />
            <AlertTitle>La firma del índice falló</AlertTitle>
            <AlertDescription>
              <span>{p.error ?? 'Sin detalle.'} Se reintenta sola cada pocos minutos.</span>
              {canManage && <Button size="sm" variant="outline" className="mt-2" disabled={retry.isPending} onClick={() => retry.mutate(p.phase, { onSuccess: (r) => notify(r.status === 'SIGNED' ? 'success' : 'error', r.status === 'SIGNED' ? 'Índice firmado' : 'La firma volvió a fallar', r.error ?? undefined), onError: (e) => notify('error', 'No se pudo firmar', errorMessage(e)) })}>{retry.isPending ? <Spinner /> : <Icono nombre="mdi:refresh" />} Firmar de nuevo</Button>}
            </AlertDescription>
          </Alert>
        )}
        <KeyValue items={[
          { label: p.phase === 1 ? 'Abierto' : 'Reabierto', value: fmtDate(p.openedAt) },
          ...(p.openReason ? [{ label: 'Motivo de la reapertura', value: p.openReason }] : []),
          ...(p.closedAt ? [{ label: 'Cerrado', value: fmtDate(p.closedAt) }] : []),
          ...(signed ? [
            { label: 'Firmado', value: fmtDate(p.signedAt) },
            { label: 'Firmante', value: <span className="break-words">{p.signerSubject}</span> },
            { label: 'Estampa de tiempo', value: p.timestampAt ? `${fmtDate(p.timestampAt)} · ${p.timestampAuthority}` : 'Sin estampa de una TSA (hora del servidor)' },
            { label: 'Certificado al firmar', value: p.revocation ? `${REVOCATION[p.revocation.status] ?? p.revocation.status}${p.revocation.detail ? ` · ${p.revocation.detail}` : ''}` : 'No se consultó' },
            { label: 'Huella del XML firmado', value: p.xmlHash ? <Tooltip><TooltipTrigger asChild><span className="text-xs tnum" tabIndex={0}>{shortHash(p.xmlHash)}</span></TooltipTrigger><TooltipContent className="max-w-none break-all">{p.xmlHash}</TooltipContent></Tooltip> : '—' },
          ] : []),
        ]} />
        {!p.entries.length ? <EmptyState title="Sin documentos en esta fase." icon="mdi:file-document-outline" /> : (
          <Tabla
            columnas={columnas} filas={p.entries} claveFila={(e) => e.id} buscar={p.entries.length > 8} densa
            ordenInicial={{ key: 'order' }}
            agrupar={porTipo ? { por: 'type' } : undefined}
            barra={tipos > 1 ? <Label className="flex items-center gap-2 text-xs font-normal text-muted-foreground"><Switch checked={porTipo} onCheckedChange={setPorTipo} aria-label="Agrupar por tipo documental" /> Agrupar por tipo documental</Label> : undefined}
            expandir={(e) => (
              <KeyValue items={[
                { label: 'Huella SHA-256', value: <span className="tnum break-all text-xs">{e.hash}</span> },
                { label: 'Tipo documental', value: e.documentType },
                { label: 'Formato y tamaño', value: `${e.format} · ${fmtBytes(e.sizeBytes)}${e.pageCount ? ` · ${e.pageCount} página${e.pageCount === 1 ? '' : 's'}` : ''}` },
                { label: 'Ingreso', value: fmtDate(e.incorporatedAt) },
                ...(e.copyMethod ? [{ label: 'Copia', value: copyLabel(e.copyMethod, e.copyOfOrder) }] : []),
                ...(e.replacesOrder ? [{ label: 'Versión', value: versionLabel(e.documentVersion, e.replacesOrder) }] : []),
                ...(e.annulledAt ? [{ label: 'Anulado', value: `${fmtDate(e.annulledAt)} · ${e.annulReason ?? ''}` }] : []),
              ]} />
            )}
          />
        )}
      </div>
    </Card>
  );
}

function CheckRow({ c }: { c: VerificationCheck }) {
  const k = CHECK[c.status];
  return (
    <li className="flex gap-2 py-1.5">
      <Icono nombre={k.icon} className={`text-lg shrink-0 ${k.cls}`} aria-label={k.label} />
      <div className="min-w-0"><div className="text-sm">{c.label}</div>{c.detail && <div className="text-xs text-muted-foreground break-words">{c.detail}</div>}</div>
    </li>
  );
}

function PhaseReport({ v, total }: { v: PhaseVerification; total: number }) {
  const fails = [...v.structural, ...v.cryptographic, ...v.cross].filter((x) => x.status === 'FAIL').length;
  return (
    <Plegable variante="borde" defaultAbierto={fails > 0} titulo={<span className="flex items-center gap-2">{phaseTitle(v.phase, total)} {fails ? <Badge variant="destructive-light" size="sm">{fails} {fails === 1 ? 'problema' : 'problemas'}</Badge> : <Badge variant="success-light" size="sm">Sin problemas</Badge>}</span>}>
      <div className="flex flex-col gap-3">
        <section><h4 className="text-sm font-semibold m-0">Nivel 1 · Estructura del índice</h4><ul className="m-0 p-0 list-none">{v.structural.map((c) => <CheckRow key={c.code} c={c} />)}</ul></section>
        <section><h4 className="text-sm font-semibold m-0">Nivel 2 · Firma digital</h4><ul className="m-0 p-0 list-none">{v.cryptographic.map((c) => <CheckRow key={c.code} c={c} />)}</ul></section>
        <section>
          <h4 className="text-sm font-semibold m-0">Nivel 3 · Archivos contra el índice</h4>
          {!v.cross.length ? <p className="text-sm text-muted-foreground m-0">Sin documentos.</p> : (
            <ul className="m-0 p-0 list-none">{v.cross.map((r) => <CheckRow key={r.order} c={{ code: String(r.order), label: `N.º ${r.order} · ${r.fileName}`, status: r.status, detail: r.detail ?? (r.fileHash ? `SHA-256 ${shortHash(r.fileHash)}` : undefined) }} />)}</ul>
          )}
        </section>
      </div>
    </Plegable>
  );
}

function VerificationSheet({ open, onHide, run, report, error, fases, documentos }: { open: boolean; onHide: () => void; run: number; report: IndexVerificationReport | null; error: unknown; fases: number; documentos: number }) {
  /** El resultado se muestra cuando termina la presentación (o cuando la persona la salta). */
  const [mostrar, setMostrar] = useState(false);
  useEffect(() => { setMostrar(false); }, [run]);
  const listo = !!report || !!error;
  const terminar = useCallback(() => setMostrar(true), []);
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onHide(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Verificación de integridad</SheetTitle>
          <SheetDescription>{mostrar && report ? `${report.radication ?? 'Expediente'} · ${fmtDate(report.verifiedAt)}` : 'Protocolo de auditoría en tres niveles'}</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 px-4 pb-6">
          {!mostrar ? (
            <IntegrityCheckAnimation key={run} fases={fases} documentos={documentos} listo={listo} onTerminar={terminar} />
          ) : error || !report ? (
            <Alert variant="destructive" className="animate-aparecer"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>No pudimos verificar el expediente</AlertTitle><AlertDescription>{errorMessage(error)}</AlertDescription></Alert>
          ) : (
            <div className="flex flex-col gap-4 animate-aparecer">
              <Alert variant={report.ok ? 'success' : 'destructive'}>
                <Icono nombre={report.ok ? 'mdi:shield-check' : 'mdi:shield-alert'} />
                <AlertTitle>{report.ok ? 'El expediente está íntegro' : 'Se encontraron problemas'}</AlertTitle>
                <AlertDescription>{report.ok ? 'La estructura, la firma y los archivos coinciden con el índice. Los puntos con reservas no invalidan el expediente.' : 'Revisa los puntos marcados: algún archivo, firma o registro no coincide con el índice.'}</AlertDescription>
              </Alert>
              <section>
                <h3 className="text-sm font-semibold m-0 mb-1">Lista de control</h3>
                <ul className="m-0 p-0 list-none">{report.checklist.map((c) => <CheckRow key={c.code} c={c} />)}</ul>
              </section>
              {report.phases.map((v) => <PhaseReport key={v.phase} v={v} total={report.phases.length} />)}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** Cuánto se conserva el expediente (TRD) y, cumplido el plazo, que ya se puede eliminar. */
function RetentionNotice({ r }: { r: CaseRetentionView }) {
  const years = `${r.years} ${r.years === 1 ? 'año' : 'años'}`;
  const source = r.source === 'CASE_TYPE' ? 'según la TRD del tipo de caso' : 'el tiempo por defecto de la entidad';
  if (r.legalHold) return <Alert variant="warning"><Icono nombre="mdi:gavel" /><AlertTitle>Bajo retención legal</AlertTitle><AlertDescription>No se puede eliminar aunque se cumpla su tiempo de conservación ({years} desde el cierre).</AlertDescription></Alert>;
  if (!r.until) return <Alert variant="info"><Icono nombre="mdi:archive-clock-outline" /><AlertDescription>Se conserva {years} desde el cierre, {source}. La fecha queda fija cuando el expediente se cierre.</AlertDescription></Alert>;
  if (r.blockedBy === null) return <Alert variant="warning"><Icono nombre="mdi:archive-check-outline" /><AlertTitle>Cumplió su tiempo de conservación</AlertTitle><AlertDescription>Se conservaba hasta el {fmtDate(r.until, false)} ({years} desde el cierre, {source}). {r.canDispose ? 'Ya se puede eliminar.' : 'Un administrador lo puede eliminar.'}</AlertDescription></Alert>;
  return <Alert variant="info"><Icono nombre="mdi:archive-clock-outline" /><AlertDescription>Se conserva hasta el <span className="font-medium">{fmtDate(r.until, false)}</span>: {years} desde el cierre del {fmtDate(r.closedAt, false)}, {source}. Después se podrá eliminar.</AlertDescription></Alert>;
}

/** Disposición final: motivo y el radicado escrito, porque no se puede deshacer. */
function DisposeDialog({ caseId, radication, r, open, onHide }: { caseId: string; radication: string; r: CaseRetentionView; open: boolean; onHide: () => void }) {
  const dispose = useDisposeCase(caseId);
  const nav = useNavigate();
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const valid = reason.trim().length >= 10 && confirmation.trim().toUpperCase() === radication.toUpperCase();
  const close = () => { onHide(); setReason(''); setConfirmation(''); dispose.reset(); };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Eliminar expediente</DialogTitle>
          <DialogDescription>Cumplió su tiempo de conservación (hasta el {fmtDate(r.until, false)}). Se borran sus documentos y el índice del almacenamiento y el expediente deja de aparecer. No se puede deshacer: la copia del respaldo de la entidad se conserva y la auditoría guarda el acta de eliminación.</DialogDescription>
        </DialogHeader>
        <Field label={<>Motivo <span className="sr-only">obligatorio, al menos 10 caracteres</span></>} htmlFor="dispose-reason" hint="Queda en el acta de eliminación.">
          <Textarea id="dispose-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej. eliminación aprobada por el comité de archivo, acta 12 de 2036" aria-invalid={reason.length > 0 && reason.trim().length < 10} />
        </Field>
        <Field label={<>Escribe <span className="font-medium tnum">{radication}</span> para confirmar</>} htmlFor="dispose-confirm">
          <Input id="dispose-confirm" autoComplete="off" value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
        </Field>
        {dispose.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(dispose.error)}</AlertDescription></Alert>}
        <DialogFooter>
          <Button variant="ghost" onClick={close}>Cancelar</Button>
          <Button variant="destructive" disabled={!valid || dispose.isPending} onClick={() => dispose.mutate({ reason: reason.trim(), confirmation: confirmation.trim() }, {
            onSuccess: (res) => {
              notify(res.filesNotDeleted ? 'warn' : 'success', 'Expediente eliminado', res.filesNotDeleted ? `${res.filesNotDeleted} archivos no se pudieron borrar del almacenamiento; quedan anotados en la auditoría.` : undefined);
              close();
              nav('/pqrs');
            },
          })}>{dispose.isPending ? <Spinner /> : <Icono nombre="mdi:delete-forever-outline" />} Eliminar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
